import React, {FC, useEffect} from 'react';

import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {AsyncMoreStatusesEnum} from '../../../../store/appTypes';
import {
  externalEndpointsErrorsLabels,
  getExternalEndpointErrorType,
  validateExternalEndpoint,
} from '../../../editors/commons/externalEndpointErrors';
import {isStringOrTemplate} from '../../../editors/commons/stringTemplateModel';
import {getParameters} from '../../../editors/expressions/expressionInferHelpers';
import {usePreviewerEditorContext} from '../../../editors/previewerEditor/previewerEditorContext';
import {useFetchServers} from '../../../fetchers/servers';
import {RuleMultilineParameterCard} from '../ruleMultilineParameterCard';
import {AtomCardProps} from './atomCard';
import {AtomIcon} from './atomIcon';
import {AtomTitle} from './atomTitle';
import {StyledParameterLineDiv} from './parameterLine';
import {AtomParameterPicker} from './pickers/atomParameterPicker';

export const FetchAtomCard: FC<AtomCardProps> = (props) => {
  const {atom, isDisabled, onUpdateAtom, onCancel} = props;
  const [endpointParam] = getParameters(atom);
  const localize = useLocalize();

  // We can only request from servers that are registered in the application.
  // This means that this atom can only be used by application components (for now, only previewers).
  const {application} = usePreviewerEditorContext();
  const {items: servers, moreStatus: serversStatus} = useFetchServers(application.url, '');

  useEffect(() => {
    const {endpoint} = atom.params;
    if (!isStringOrTemplate(endpoint)) {
      return;
    }

    const reason = validateExternalEndpoint(
      endpoint,
      serversStatus === AsyncMoreStatusesEnum.ALL_LOADED ? servers : undefined,
    );

    const errorMessage = reason && localize(externalEndpointsErrorsLabels[reason]);
    if (errorMessage === atom.errorMessage) {
      return;
    }

    const errorType = reason && getExternalEndpointErrorType(reason);

    if (errorMessage !== atom.errorMessage) {
      onUpdateAtom({
        ...atom,
        errorMessage,
        errorType,
      });
    }
  }, [atom, localize, onUpdateAtom, servers, serversStatus]);

  return (
    <RuleMultilineParameterCard
      errorMessage={atom.errorMessage}
      errorType={atom.errorType}
      title={<AtomTitle atom={atom} />}
      icon={<AtomIcon atom={atom} />}
      onCancel={onCancel}
    >
      <StyledParameterLineDiv>
        <AtomParameterPicker
          parameter={endpointParam}
          atom={atom}
          isDisabled={isDisabled}
          onUpdate={onUpdateAtom}
        />
      </StyledParameterLineDiv>
    </RuleMultilineParameterCard>
  );
};
